/**
 * Toast.tsx — Componente de notificación transitoria del design system
 *
 * Patrón: Radix Toast (primitivo) + CVA (variantes) + Tailwind + Lucide.
 * Sigue la referencia canónica de Button.tsx (ADR-025).
 * Primer consumidor real: H3.4 "Copiar enlace cliente" en PortalTokensPanel.
 *
 * Por qué Radix Toast:
 *   Provee role="status"/role="alert", aria-live automático, temporizador
 *   pausable en hover/foco, portal, swipe-to-dismiss y navegación por
 *   teclado sin implementación manual (ADR-025, planificado Semana 2 E1).
 *
 * Exports públicos:
 *   ToastProvider — envuelve la app (o la subtree) una sola vez.
 *   ToastViewport — posición fija en pantalla (bottom-4 right-4).
 *   Toast — Root de cada notificación, acepta variant + open/onOpenChange.
 *   ToastTitle — Título de la notificación.
 *   ToastDescription — Descripción opcional.
 *   ToastClose — Botón X para cerrar manualmente.
 *   useToast — Hook para disparar toasts desde cualquier componente.
 *
 * Uso típico:
 *   // 1. En la raíz del árbol (App.tsx):
 *   <ToastProvider><App/><ToastViewport /></ToastProvider>
 *
 *   // 2. En cualquier componente:
 *   const { toast } = useToast();
 *   toast({ title: 'Enlace copiado', duration: 3000 });
 *
 * [WCAG 4.1.3 — Mensajes de estado]
 *   Radix Toast gestiona role="status" y aria-live="polite" automáticamente
 *   en el Viewport. El contenido es anunciado por AT sin focus trap.
 */
import * as React from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { X, Check, AlertTriangle, Info } from 'lucide-react';
import { cn } from './utils';

// ---------------------------------------------------------------------------
// Variantes CVA
// ---------------------------------------------------------------------------

const toastVariants = cva(
  // Base: z-50, fondo sólido, borde, sombra, flex, gap, padding, ancho max.
  // transition-[opacity,transform]: animación de entrada/salida sin tailwindcss-animate.
  // data-[state=open]: visible; data-[state=closed]: fade out.
  [
    'group pointer-events-auto relative flex items-start gap-3',
    'w-full max-w-sm rounded-[12px] border p-4 shadow-cumple-soft',
    'transition-[opacity,transform] duration-300',
    // Entrada desde la derecha
    'data-[state=open]:opacity-100 data-[state=open]:translate-x-0',
    // Salida: fade + slide
    'data-[state=closed]:opacity-0 data-[state=closed]:translate-x-4',
    // Swipe-to-dismiss (Radix gestiona el gesto)
    'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
    'data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-[transform_200ms_ease-out]',
    'data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]',
  ],
  {
    variants: {
      variant: {
        /**
         * default — Notificación informativa. Fondo oscuro (cumple-primary-900)
         * sobre texto blanco: contraste 19:1 (UX spec H3.4).
         */
        default: ['bg-cumple-primary-900 text-white border-cumple-primary-700'],
        /**
         * success — Confirmación positiva. Fondo success-600 tinted.
         */
        success: ['bg-cumple-success-600 text-white border-cumple-success-600/20'],
        /**
         * warning — Alerta no crítica. Fondo warning-500 tinted.
         */
        warning: ['bg-cumple-warning-500 text-white border-cumple-warning-500/20'],
        /**
         * destructive — Error o acción irreversible. Fondo danger-500.
         */
        destructive: ['bg-cumple-danger-500 text-white border-cumple-danger-500/20'],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

// ---------------------------------------------------------------------------
// ToastProvider — re-export directo (gestiona el estado global de toasts)
// ---------------------------------------------------------------------------

/**
 * ToastProvider — Debe envolver la subtree donde se usen toasts.
 * Acepta duration (ms) como prop global; cada toast puede sobrescribirlo.
 */
const ToastProvider = ToastPrimitive.Provider;
ToastProvider.displayName = 'ToastProvider';

// ---------------------------------------------------------------------------
// ToastViewport — Contenedor posicionado fijo en pantalla
// ---------------------------------------------------------------------------

/**
 * ToastViewport — Posición fixed bottom-4 right-4. Solo debe aparecer UNA
 * vez en el árbol. Renderiza fuera del DOM (portal interno de Radix).
 *
 * [WCAG 4.1.3] Radix aplica role="region" y aria-label="Notifications" al
 * Viewport automáticamente para que AT pueda navegar a él.
 */
const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Viewport
    ref={ref}
    className={cn(
      // Posición y z-index: sobre modales (z-50 → z-[60])
      'fixed bottom-4 right-4 z-[60]',
      // Stack vertical de toasts con gap
      'flex flex-col gap-2',
      // Ancho fijo para que los toasts sean consistentes
      'w-full max-w-sm',
      // Sin outline (el Viewport no es interactivo directamente)
      'outline-none',
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = 'ToastViewport';

// ---------------------------------------------------------------------------
// Toast — Root de la notificación individual
// ---------------------------------------------------------------------------

export interface ToastProps
  extends
    React.ComponentPropsWithoutRef<typeof ToastPrimitive.Root>,
    VariantProps<typeof toastVariants> {}

/**
 * Toast — Contenedor de una notificación individual.
 * Radix gestiona: open/close, duración, swipe, aria-live, role.
 */
const Toast = React.forwardRef<React.ElementRef<typeof ToastPrimitive.Root>, ToastProps>(
  ({ className, variant, children, ...props }, ref) => (
    <ToastPrimitive.Root ref={ref} className={cn(toastVariants({ variant }), className)} {...props}>
      {children}
    </ToastPrimitive.Root>
  ),
);
Toast.displayName = 'Toast';

// ---------------------------------------------------------------------------
// ToastTitle — Texto principal de la notificación
// ---------------------------------------------------------------------------

/**
 * ToastTitle — Texto principal. Radix enlaza con aria-labelledby del root.
 * Texto blanco, tamaño sm, font-medium.
 */
const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Title
    ref={ref}
    className={cn('text-sm font-semibold leading-snug', className)}
    {...props}
  />
));
ToastTitle.displayName = 'ToastTitle';

// ---------------------------------------------------------------------------
// ToastDescription — Descripción opcional
// ---------------------------------------------------------------------------

/**
 * ToastDescription — Texto secundario opcional. Radix enlaza con
 * aria-describedby del root cuando está presente.
 */
const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Description
    ref={ref}
    className={cn('text-xs opacity-80 mt-0.5', className)}
    {...props}
  />
));
ToastDescription.displayName = 'ToastDescription';

// ---------------------------------------------------------------------------
// ToastClose — Botón de cierre manual
// ---------------------------------------------------------------------------

/**
 * ToastClose — Botón X en la esquina superior derecha.
 * min-h/w 44px respetan WCAG 2.5.5. Lucide X como ícono.
 */
const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Close
    ref={ref}
    aria-label="Cerrar notificación"
    className={cn(
      // Área táctil WCAG 2.5.5
      'inline-flex items-center justify-center shrink-0',
      'h-7 w-7 rounded-md',
      'opacity-70 hover:opacity-100 transition-opacity',
      // Focus ring igual al resto del sistema
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1',
      className,
    )}
    {...props}
  >
    <X className="h-4 w-4" aria-hidden="true" />
  </ToastPrimitive.Close>
));
ToastClose.displayName = 'ToastClose';

// ---------------------------------------------------------------------------
// Íconos predefinidos por variante (consumibles opcionalmente)
// ---------------------------------------------------------------------------

/**
 * ToastIcon — Ícono visual por variante. Decorativo (aria-hidden).
 * Uso: <ToastIcon variant="success" /> dentro de <Toast variant="success">
 */
export function ToastIcon({ variant }: { variant: ToastProps['variant'] }): JSX.Element {
  const cls = 'h-5 w-5 shrink-0 mt-0.5';
  switch (variant) {
    case 'success':
      return <Check className={cls} aria-hidden="true" />;
    case 'warning':
      return <AlertTriangle className={cls} aria-hidden="true" />;
    case 'destructive':
      return <AlertTriangle className={cls} aria-hidden="true" />;
    default:
      return <Info className={cls} aria-hidden="true" />;
  }
}

// ---------------------------------------------------------------------------
// useToast — Hook para disparar toasts de forma imperativa
// ---------------------------------------------------------------------------

interface ToastConfig {
  title: string;
  description?: string;
  variant?: ToastProps['variant'];
  /** Duración en milisegundos. Default: 3000ms (H3.4 exige 3s). */
  duration?: number;
}

interface ToastState extends ToastConfig {
  id: string;
  open: boolean;
}

interface UseToastReturn {
  toasts: ToastState[];
  toast: (config: ToastConfig) => void;
  dismiss: (id: string) => void;
}

/**
 * useToast — Gestiona una lista de toasts activos.
 *
 * Devuelve:
 *   toasts   — array de estados actuales
 *   toast    — función para añadir una nueva notificación
 *   dismiss  — función para cerrar una notificación por id
 *
 * El ToastViewport/ToastProvider debe estar montado en la subtree del
 * componente que consuma useToast para que los toasts sean visibles.
 *
 * @example
 *   const { toast } = useToast();
 *   toast({ title: 'Enlace copiado', variant: 'success', duration: 3000 });
 */
export function useToast(): UseToastReturn {
  const [toasts, setToasts] = React.useState<ToastState[]>([]);

  const toast = React.useCallback((config: ToastConfig) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setToasts((prev) => [...prev, { ...config, id, open: true }]);
  }, []);

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, open: false } : t)));
    // Limpia del array tras la animación de salida (300ms)
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 400);
  }, []);

  return { toasts, toast, dismiss };
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  toastVariants,
};
